import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link,
  useActionData,
  useNavigate,
  useNavigation,
  useSearchParams,
  useSubmit,
} from "react-router-dom";

import ProgressBar from "@/atoms/progress-bar/ProgressBar";
import RepeatCashAvailabilityBanner from "@/atoms/repeat-cash/RepeatCashBanner";
import { removeNonDigits } from "@/lib/utils";
import {
  ActionResult,
  CartBackButton,
  cartMeetsMinimumSpendRequirementForReward,
  CartTopNav,
  discountAppliedMessage,
  MEMBERSHIP_BENEFIT_KEY,
} from "@/modules/cart";
import { GiftCardRedemptionModal } from "@/modules/giftCards";
import { queryClient } from "@/pages/Root";
import { LocationDetailsCard } from "@/pages/root/cart/LocationDetailsCard";
import { RewardUnlockBanner } from "@/pages/root/cart/RewardUnlockBanner";
import { UnableToCheckoutModal } from "@/pages/root/cart/UnableToCheckoutModal";
import { get, post } from "api";
import { Models, ShopDisplayType } from "api/types";
import {
  AFFIRM_MINIMUM_AMOUNT,
  AffirmUnavailableReason,
  NotAvailableAffirm,
} from "atoms/affirm/NotAvailableAffirm/NotAvailableAffirm";
import { ShopNowAffirm } from "atoms/affirm/shopNowAffirm/ShopNowAffirm";
import { Button } from "atoms/button/Button";
import { Dropdown } from "atoms/dropdown/Dropdown";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import FrameIcon from "atoms/icon/icons/frame.svg";
import InfoIcon from "atoms/icon/icons/info.svg";
import WarningIcon from "atoms/icon/icons/warning_triangle.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { Spinner } from "atoms/Spinner";
import { TermsAndCondition } from "atoms/terms-and-condition/TermsAndCondition";
import { ApplyRepeatMDCash } from "molecules/containers/cart/apply-repeatmd-cash/ApplyRepeatMDCash";
import { AvailablePromotions } from "molecules/containers/cart/available-promotions/AvailablePromotions";
import { CartItemProps } from "molecules/containers/cart/cart-item/CartItem";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import { CartItemList } from "organisms/cart-item-list/CartItemList";
import { OrderSummary } from "organisms/order-summary/OrderSummary";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import { IfFlag, useFeatureFlag } from "services/Flags";
import {
  getBXGYDiscountText,
  getProductTitle,
  isBuyXGetYDiscount,
} from "services/HelperService";
import { notNull } from "toolbox/Guards";
import { formatCurrency, parsePriceString } from "toolbox/Money";
import {
  formatBillingPeriodInterval,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";
import { getPDPUrl, removeArchiveItems, useStorage } from "utils/functions";

import {
  invalidateCurrentOrderDetails,
  useCurrentOrderDetails,
} from "../useCartDetails";
import { useManageCartAppointments } from "../useManageCartAppointments";

const ShippingNotification = (props: {
  threshold?: number | null;
  current: number;
  eligible: boolean;
}) => {
  if (!props.threshold) return null;

  if (props.eligible) {
    return (
      <div className="flex items-center gap-3 bg-brand-color p-3 text-white">
        <Icon svg={FrameIcon} className="fill-current" size="xsmall" />
        <span className="mr-4 flex grow flex-col gap-3 text-bold2">
          Free shipping unlocked 🚀
          <ProgressBar value={100} progressClassName="bg-brand-color/60" />
        </span>
      </div>
    );
  }

  const amountLeft = props.threshold - props.current;
  return (
    <Link
      to="/shop/products"
      className="flex items-center gap-3 bg-brand-color p-3 text-white"
    >
      <Icon svg={FrameIcon} className="fill-current" size="xsmall" />

      <span className="flex flex-col gap-1 text-bold2">
        Add {formatCurrency(amountLeft)} to your cart to unlock free shipping
        <ProgressBar
          value={props.current}
          max={props.threshold}
          progressClassName="bg-brand-color/60"
        />
        <span className="text-body3">Tap to continue shopping</span>
      </span>

      <Icon svg={ChevronRight} className="fill-current" size="xsmall" />
    </Link>
  );
};

type CartContainerProps = {
  platform: Models<"PlatformModel">;
  salesAssociates: Models<"SalesAssociateModel">[];
  showNavbar: boolean;
  membershipDiscount: Models<"GetMembershipDetailsResult"> | null;
  cart: Models<"ShopDisplayOrderResult">;
  initialLineItems: Models<"CartLineItemModel">[];
  listings: Models<"ListingModel">[];
  initialLocations: Models<"GetPlatformLocationsResult">[];
};

export const CartContainer = ({
  platform,
  salesAssociates,
  showNavbar,
  membershipDiscount,
  initialLocations,
  cart,
  initialLineItems,
  listings,
}: CartContainerProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const giftCardRecipientMissingModal = searchParams.get(
    "giftCardRecipientMissing"
  );
  const shopDisplayOrderLineItemId = searchParams.get(
    "shopDisplayOrderLineItemId"
  );

  const showGiftCardRecipientMissingModal =
    giftCardRecipientMissingModal === "true" &&
    shopDisplayOrderLineItemId !== null;

  const [storedMembership, setStoredMembership] =
    useStorage<Models<"GetMembershipDetailsResult"> | null>(
      MEMBERSHIP_BENEFIT_KEY,
      null
    );

  const isSelectPatientLocationEnabled = useFeatureFlag(
    "selectPatientLocation"
  );
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );

  const { state } = useNavigation();

  const { data: platformUser } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
  });

  const {
    data: cartDetails,
    isFetching,
    isLoading,
  } = useCurrentOrderDetails({
    initialData: cart,
    staleTime: 5 * 60 * 1000,
  });

  const { data: lineItems = [] } = useQuery({
    queryKey: ["/orders/current/lineitems"],
    queryFn: () => get("/orders/current/lineitems"),
    initialData: initialLineItems,
  });

  const { data: locations } = useQuery({
    queryKey: ["platform/locations"],
    queryFn: async () => {
      const response = await get("/platform/locations", {});
      return response.items;
    },
    initialData: initialLocations,
  });

  const mutateCreateAndLinkGiftCardRecipient = useMutation({
    mutationFn: async ({
      shopDisplayOrderLineItemId,
      phoneNumber,
      email,
      firstName,
      lastName,
      giftCardItemId,
      mySelf,
    }: {
      shopDisplayOrderLineItemId: string;
      phoneNumber: string;
      email: string;
      firstName: string;
      lastName: string;
      giftCardItemId: string;
      mySelf: boolean;
    }) => {
      // First create the transfer
      const packageTransferId = await post("/gift-cards/transfer", {
        platformId: platform.id,
        giftCardItemId: giftCardItemId,
        senderPlatformUserId: cartDetails?.platformUser?.id ?? "",
        senderPhoneNumber: platformUser?.phoneNumber ?? "",
        senderEmail: platformUser?.email ?? "",
        senderFirstName: cartDetails?.platformUser?.fistName ?? "",
        senderLastName: cartDetails?.platformUser?.lastName ?? "",
        recipientPlatformUserId: mySelf
          ? cartDetails?.platformUser?.id ?? ""
          : null,
        recipientPhoneNumber: mySelf
          ? platformUser?.phoneNumber ?? ""
          : phoneNumber ?? "",
        recipientEmail: email ?? "",
        recipientFirstName: firstName ?? "",
        recipientLastName: lastName ?? "",
      });

      // Then link it to the order
      await post(
        "/gift-cards/transfers/{packageTransferId}/link-to-order/{shopDisplayOrderLineItemId}",
        {
          packageTransferId: packageTransferId.value,
          shopDisplayOrderLineItemId: shopDisplayOrderLineItemId,
        }
      );

      return packageTransferId;
    },
    onSuccess: () => {
      setSearchParams({}, { replace: true });

      queryClient.invalidateQueries({
        queryKey: ["/gift-cards/transfers/by-order/{orderId}"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/gift-cards/transfers"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/gift-cards"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/rewards"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/orders/current/available-promos"],
      });
      queryClient.invalidateQueries({
        queryKey: ["platform/locations"],
      });
      invalidateCurrentOrderDetails();
    },
  });

  const { data: rewards } = useQuery({
    queryKey: ["/rewards"],
    queryFn: () => get("/rewards", {}),
  });

  // Re-create the "unavailableMembership" variable
  const unavailableMembership = membershipDiscount?.membershipName
    ? undefined
    : storedMembership ?? undefined;

  const { data: availablePromos } = useQuery({
    queryKey: ["/orders/current/available-promos"],
    queryFn: () => get("/orders/current/available-promos"),
  });

  const containsGiftCard = cartDetails?.shopDisplayOrderLineItems?.some(
    (item) => item.displayType === ShopDisplayType.GiftCard
  );

  const { data: giftCardTransfers } = useQuery({
    queryKey: [
      "/gift-cards/transfers/by-order/{orderId}",
      { orderId: cartDetails?.shopDisplayOrderId },
    ],
    queryFn: () =>
      get("/gift-cards/transfers/by-order/{orderId}", {
        orderId: cartDetails?.shopDisplayOrderId ?? "",
      }),
    enabled: !!cartDetails?.shopDisplayOrderId && containsGiftCard,
  });

  const { reservationTimeRemaining, isLineItemBeingDeleted } =
    useManageCartAppointments({
      cartDetails,
      partnerEmrBookingEnabled:
        platform.platformSettings?.partnerEmrBookingEnabled,
    });

  const itemIdMap = useQueries({
    queries:
      cartDetails?.shopDisplayOrderLineItems?.map((item) => ({
        queryKey: ["/shop/displays/{shopDisplayId}", item.shopDisplayId],
        queryFn: () =>
          get("/shop/displays/{shopDisplayId}", {
            shopDisplayId: item.shopDisplayId,
          }),
      })) || [],
  }).reduce((acc, item) => {
    if (!item.data) {
      return { ...acc };
    }
    return { ...acc, [item.data.id]: item.data };
  }, {} as Record<string, Models<"GetShopDisplayResult">>);
  const promoCode = cartDetails?.discountSummary?.discounts?.find(
    (discount) => discount.promoCode
  );

  const discountMessage = discountAppliedMessage(
    promoCode,
    cartDetails?.shopDisplayOrderLineItems || []
  );
  const navigate = useNavigate();
  const actionData = useActionData() as ActionResult | undefined;
  const _submit = useSubmit();
  const submit = useCallback<typeof _submit>(
    (args, opts) => _submit(args, { ...opts, replace: true }),
    [_submit]
  );
  const [actionSuccessMessage, setActionSuccessMessage] = useState<
    string | undefined
  >();

  const successMessage = actionSuccessMessage || discountMessage;
  const [shopDisplay, setShopDisplay] =
    useState<Models<"GetShopDisplayResult">>();
  const [originalPackagePriceId, setOriginalPackagePriceId] =
    useState<string>("");
  const [newPackagePriceId, setNewPackagePriceId] = useState<string>("");
  const [editShopDisplayOrderLineItemId, setEditShopDisplayOrderLineItemId] =
    useState<string>("");
  const [originalShopDisplayPackageId, setOriginalShopDisplayPackageId] =
    useState<string>("");
  const [newShopDisplayPackageId, setNewShopDisplayPackageId] =
    useState<string>("");
  const [isCheckingOut, setIsCheckingOut] = useState<boolean>(false);

  const [hasMoreDiscounts, setHasMoreDiscounts] = useState<boolean>(false);
  const disableApplyButton =
    originalPackagePriceId == newPackagePriceId || newPackagePriceId == "";

  const packages = shopDisplay?.shopDisplayPackages ?? [];

  const packagesPrices =
    shopDisplay?.shopDisplayPackages?.find(
      (item) => item.id == newShopDisplayPackageId
    )?.package?.packagePrices ?? [];

  const [preventCheckout, setPreventCheckout] = useState<boolean>(true);

  type Modal =
    | { type: "select-units" }
    | { type: "select-treatment" }
    | { type: "select-promo-code" }
    | { type: "repeatcash-invalid" }
    | { type: "expired-listing" }
    | { type: "buy-x-get-y-offer" }
    | null;

  const hasExpiredItem = lineItems.some((li) => li.expired);
  const [modal, setModal] = useState<Modal>(
    hasExpiredItem ? { type: "expired-listing" } : null
  );

  const [loading, setLoading] = useState<boolean>(false);

  const coupon = useMemo(
    () => promoCode?.promoCode ?? "",
    [promoCode?.promoCode]
  );
  const rewardId = useMemo(
    () => promoCode?.rewardId ?? "",
    [promoCode?.rewardId]
  );
  const discountId = useMemo(
    () => promoCode?.discountId,
    [promoCode?.discountId]
  );
  const couponAmountOff = useMemo(() => {
    if (!promoCode) return 0;
    return promoCode.amountOff > 0
      ? promoCode.amountOff
      : promoCode.membershipsDiscountTotal ?? 0;
  }, [promoCode]);
  const buyXGetYFlag = useFeatureFlag("BuyXGetY");

  const [isLocationChangeModalOpen, setIsLocationChangeModalOpen] =
    useState(false);
  const salesAssociateValue = cartDetails?.salesAssociateId ?? "";
  const salesAssociateValueText = useMemo(() => {
    if (!salesAssociates || !salesAssociateValue) return "";

    const salesAssociate = salesAssociates.find(
      (associate) => associate.id === salesAssociateValue
    );

    return salesAssociate
      ? `${salesAssociate.firstName} ${salesAssociate.lastName}`
      : "";
  }, [salesAssociateValue, salesAssociates]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    cartDetails?.selectedLocationId ?? ""
  );
  const [shouldDisplayPromotionModal, setShouldDisplayPromotionModal] =
    useState(false);
  const [hideUnavailableLocationModal, setHideUnavailableLocationModal] =
    useState(false);
  const [hideInvalidPromotionModal, setHideInvalidPromotionModal] =
    useState(false);

  const displayedLocation = useMemo(() => {
    if (!cartDetails?.selectedLocationId || !locations?.length) {
      return undefined;
    }
    return locations.find(
      (location) => location.id === cartDetails.selectedLocationId
    );
  }, [cartDetails?.selectedLocationId, locations]);

  const allItemsAvailableAtSelectedLocation = useMemo(() => {
    if (!displayedLocation) return false;
    return lineItems.every((item) =>
      item.availableLocations?.includes(displayedLocation.id)
    );
  }, [displayedLocation, lineItems]);

  const shouldShowUnavailableLocationModal = useMemo(() => {
    if (hideUnavailableLocationModal) return false;
    return displayedLocation && lineItems.length > 0 && !isFetching
      ? !allItemsAvailableAtSelectedLocation
      : false;
  }, [
    hideUnavailableLocationModal,
    displayedLocation,
    lineItems.length,
    isFetching,
    allItemsAvailableAtSelectedLocation,
  ]);

  const removeDisountIfRewardMinimumSpendNotMet = useCallback(async () => {
    if (!cartDetails?.discountSummary?.discounts || !rewards) return;

    const appliedRewards = cartDetails.discountSummary.discounts.filter(
      (discount) => discount.rewardId
    );

    for (const appliedReward of appliedRewards) {
      const reward = rewards.find((r) => r.rewardId === appliedReward.rewardId);

      if (reward && reward.requireMinimumSpendInCart) {
        const currentCartTotal = cartDetails.estimatedTotal;

        const meetsMinimumSpend = cartMeetsMinimumSpendRequirementForReward(
          currentCartTotal,
          reward
        );

        if (!meetsMinimumSpend) {
          submit(
            {
              action: "removePromoCodeAction",
              promoCodeId: appliedReward.promoCodeId ?? "",
            },
            {
              method: "delete",
            }
          );
        }
      }
    }
  }, [
    cartDetails?.discountSummary?.discounts,
    cartDetails?.estimatedTotal,
    rewards,
    submit,
  ]);

  useEffect(() => {
    if (rewards && cartDetails?.shopDisplayOrderLineItems?.length) {
      removeDisountIfRewardMinimumSpendNotMet();
    }
  }, [
    cartDetails?.shopDisplayOrderLineItems?.length,
    rewards,
    removeDisountIfRewardMinimumSpendNotMet,
  ]);

  useEffect(() => {
    if (actionData?.action === "promoCodeAction") {
      setLoading(false);
      setActionSuccessMessage(actionData.success);
      setModal(null);
    }
  }, [actionData]);

  useEffect(() => {
    if (
      cartDetails?.platformUser?.id &&
      membershipDiscount?.availableLocations
    ) {
      setStoredMembership(membershipDiscount);
    } else if (membershipDiscount?.membershipName) {
      setStoredMembership(null);
    }
  }, [cartDetails?.platformUser?.id, membershipDiscount, setStoredMembership]);

  const isAppliedPromoOrRewardAvailableAtSelectedLocation = (
    appliedPromoCode: string,
    appliedDiscountId: string,
    availablePromotions: Models<"RewardPromo">[] | null | undefined,
    hasInvalidMembership: boolean,
    cartDiscounts?: Models<"ShopDisplayOrderDiscountSummaryLineItem">[] | null,
    promoCodeError?: string | null | undefined
  ) => {
    if (!availablePromotions) {
      return true;
    }

    if (promoCodeError) {
      return false;
    }

    //The function returns `true` only when:
    // 1. We find a match - The cart details from /current/details contain a discount that corresponds to what the user applied
    // 2. The discount is valid when applied from available promotions
    const hasValidDiscountInCart = cartDiscounts?.some(
      (discount) =>
        (discount.promoCode === appliedPromoCode ||
          discount.discountId === appliedDiscountId) &&
        (discount.promoCode || discount.rewardId || discount.discountId)
    );

    if (hasValidDiscountInCart) {
      return true;
    }

    const hasValidPromo = availablePromotions.some(
      (promo) =>
        promo.promoCode === appliedPromoCode ||
        promo.discountId === appliedDiscountId
    );

    const hasAppliedPromo = Boolean(appliedPromoCode || appliedDiscountId);

    /*
     * Result is TRUE when:
     *  • a valid promo/reward is applied, OR
     *  • no promo/reward is applied AND membership is not invalid
     */
    return (
      hasValidDiscountInCart ||
      hasValidPromo ||
      (!hasInvalidMembership && !hasAppliedPromo)
    );
  };

  const hasInvalidMembershipApplied =
    !!unavailableMembership && membershipDiscount?.membershipName == null;

  const discountSummary = cartDetails?.discountSummary;
  const discounts = discountSummary?.discounts;
  const promoCodeError = discountSummary?.promoCodeError;

  const hasValidPromoRewardOrMembershipBenefit =
    isAppliedPromoOrRewardAvailableAtSelectedLocation(
      coupon,
      discountId ?? "",
      availablePromos,
      hasInvalidMembershipApplied,
      discounts,
      promoCodeError
    );

  const isInvalidPromotionAppliedModal = useMemo(() => {
    if (hideInvalidPromotionModal) return false;
    return (
      allItemsAvailableAtSelectedLocation &&
      (promoCodeError != null || hasInvalidMembershipApplied) &&
      !hasValidPromoRewardOrMembershipBenefit
    );
  }, [
    hideInvalidPromotionModal,
    allItemsAvailableAtSelectedLocation,
    hasValidPromoRewardOrMembershipBenefit,
    promoCodeError,
    hasInvalidMembershipApplied,
  ]);

  if ((isLoading || isLineItemBeingDeleted) && !cartDetails) {
    return (
      <div className="absolute inset-0 flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (
    !cartDetails ||
    !cartDetails.shopDisplayOrderLineItems ||
    cartDetails?.shopDisplayOrderLineItems?.length == 0
  ) {
    return (
      <CartBackButton
        placeholder="Your shopping cart is empty"
        showNavbar={showNavbar}
      />
    );
  }

  const containsMembership =
    cartDetails.shopDisplayOrderLineItems?.find(
      (item) => item.shopDisplayMembershipId != null
    ) != null;

  const containsPackageOrTreatment =
    cartDetails.shopDisplayOrderLineItems?.some(
      (item) => item.shopDisplayPackageId != null
    );

  const locationName =
    isSelectPatientLocationEnabled && displayedLocation
      ? locations?.find(({ id }) => id === displayedLocation?.id)?.name ?? ""
      : undefined;

  let giftCardRecipientMissing = false;
  const giftCardListing = cartDetails.shopDisplayOrderLineItems?.filter(
    (item) => item.displayType === ShopDisplayType.GiftCard
  );
  const hasGiftCardMissingRecipient =
    cartDetails.shopDisplayOrderLineItems?.some(
      (item) =>
        item.displayType === ShopDisplayType.GiftCard && !item.packageTransferId
    ) || false;
  let giftCardPrice = 0;
  const cartItems: CartItemProps[] = cartDetails.shopDisplayOrderLineItems
    ? cartDetails.shopDisplayOrderLineItems?.filter(Boolean).map((item) => {
        const itemTitle = getProductTitle(item.productName, item.name);
        const lineItem = lineItems.find((li) => li.id === item.id);
        const selectedLocationId = cartDetails.selectedLocationId;
        const isLocationAvailable =
          lineItem?.availableLocations?.includes(selectedLocationId) ?? true;

        const parsedBillingPeriodInterval = parseBillingPeriodName(
          item.membershipBillingPeriodInterval ?? 1
        );

        if (
          item.displayType === ShopDisplayType.GiftCard &&
          !item.packageTransferId
        ) {
          // for now there should be only one gift card in the cart without a transfer (if that the case)
          giftCardPrice = item.cost;
        }

        const formatMembershipCost = (cost: number) => {
          return isFlexibleMembershipsEnabled
            ? formatBillingPeriodInterval(
                cost,
                parsedBillingPeriodInterval,
                item.membershipBillingPeriodIntervalCount
              )
            : `${formatCurrency(cost)}/month`;
        };

        const getGiftCardRecipient = (
          transfer: Models<"DisplayGiftCardTransferItem">
        ): string => {
          const isSenderSameAsRecipient =
            transfer.senderPlatformUserId === transfer.recipientPlatformUserId;

          return isSenderSameAsRecipient
            ? "For myself"
            : `To: ${transfer.recipientInformation}`;
        };

        const getPackageTitle = (
          item: Models<"ShopDisplayOrderLineItemModel">
        ): string => {
          if (item.displayType === ShopDisplayType.GiftCard) {
            const itemGiftCardTransfer = giftCardTransfers?.find(
              (transfer) => item.packageTransferId === transfer.id
            );

            if (itemGiftCardTransfer) {
              return getGiftCardRecipient(itemGiftCardTransfer);
            }
            giftCardRecipientMissing = true;
            return "Needs recipient";
          }

          if (item.displayType === ShopDisplayType.Membership) {
            return `Renews at ${formatMembershipCost(item.cost)}`;
          }

          const unitType =
            item.quantity === 1
              ? item.package?.packageUnitType.singularUnitType ?? "unit"
              : item.package?.packageUnitType.pluralUnitType ?? "units";

          return `${item.quantity} ${unitType}`;
        };

        return {
          id: item.id,
          imgUrl: item.imageUrl,
          type: item.displayType,
          discount:
            discounts?.find(
              (discountItem) =>
                discountItem.shopDisplayOrderLineItemId === item.id
            )?.packagesDiscountTotal ||
            item.discountTotal ||
            0,
          title:
            item.displayType == ShopDisplayType.GiftCard
              ? `${formatCurrency(item.cost).split(".")[0]} Gift Card`
              : itemTitle,
          price: item.cost,
          expiresOn: lineItem?.expiresOn ? dayjs(lineItem.expiresOn) : null,
          expired: lineItem?.expired ?? false,
          repeatCashValue: item.package?.repeatCashValue,
          packageTitle: getPackageTitle(item),
          showEditButton:
            item.displayType !== ShopDisplayType.GiftCard &&
            item.displayType != ShopDisplayType.Membership &&
            ((itemIdMap[item.shopDisplayId]?.shopDisplayPackages?.length ?? 0) >
              1 ||
              (itemIdMap[item.shopDisplayId]?.shopDisplayPackages?.[0]?.package
                ?.packagePrices?.length ?? 0) > 1),
          url: giftCardRecipientMissing
            ? `/cart`
            : getPDPUrl(item.displayType, item.shopDisplayId),
          onClickUrl: giftCardRecipientMissing
            ? () => {
                setSearchParams(
                  {
                    giftCardRecipientMissing: "true",
                    shopDisplayOrderLineItemId: item.id,
                  },
                  { replace: true }
                );
              }
            : undefined,
          locationName:
            !isLocationAvailable && locationName ? locationName : undefined,
          platformLogoImage: platform.mobileAppIconUrlMedium ?? undefined,
          giftCardDesignName: item.name ?? undefined,
          onEdit: async () => {
            const shopDisplayItem = itemIdMap[item.shopDisplayId];
            setShopDisplay(shopDisplayItem);
            setEditShopDisplayOrderLineItemId(item.id);
            item.shopDisplayPackageId &&
              setOriginalShopDisplayPackageId(item.shopDisplayPackageId);
            item.shopDisplayPackageId &&
              setNewShopDisplayPackageId(item.shopDisplayPackageId);
            item.packagePriceId &&
              setOriginalPackagePriceId(item.packagePriceId);
            item.packagePriceId && setNewPackagePriceId(item.packagePriceId);

            if (shopDisplayItem.shopDisplayPackages?.length === 1) {
              setModal({ type: "select-units" });
            } else {
              setModal({ type: "select-treatment" });
            }
          },
          onDelete: async () => {
            submit(
              {
                action: "deleteItem",
                shopDisplayOrderLineItemId: item.id,
              },
              { method: "delete" }
            );
            setModal(null);
            removeUnavailableMembership();
          },
          giftCardRecipientMissing,
        };
      })
    : [];

  const packageChoices = packages
    ?.filter(notNull)
    .map(
      (item) =>
        item.package && {
          id: item.id,
          title: item.package.name || "",
          description: item.package.description || "",
          priceOrResults: item.package.baseCost,
        }
    )
    .filter(notNull);

  const packagePriceChoices = packagesPrices
    ?.filter(notNull)
    .map(
      (item) =>
        item && {
          id: item.id,
          title: `${item.quantity} ${
            item.quantity > 1
              ? packages[0].package?.packageUnitType.pluralUnitType ?? "units"
              : packages[0].package?.packageUnitType.singularUnitType ?? "unit"
          }`,
          priceOrResults: item.price,
          memberPrice: item.membershipPrice,
        }
    )
    .filter(notNull);

  const defaultCheckoutNowSearchParams = { type: "all" };
  const queryString = new URLSearchParams({
    ...defaultCheckoutNowSearchParams,
    ...(!showNavbar ? { isMobileApp: "true" } : {}),
  }).toString();
  const checkoutActionPath = `/cart/checkout/element?${queryString}`;
  const isOrderTotalAboveMinimum =
    cartDetails.estimatedTotal / 100 > AFFIRM_MINIMUM_AMOUNT;

  const promoFromCartDetails = discounts?.find((d) => d.membershipId == null);
  const promoFromAvailablePromos = availablePromos?.find((promo) =>
    discounts?.find((d) => d.discountId == promo.discountId)
  );

  const getPromoCodeTitle =
    (promoFromCartDetails !== null &&
      promoFromCartDetails?.percentOff === 1 &&
      promoFromCartDetails?.membershipsDiscountTotal > 0) ||
    (promoFromAvailablePromos !== null &&
      promoFromAvailablePromos?.discount.percentOff === 1 &&
      promoFromAvailablePromos?.discount.inclusions?.some(
        (inclusion) => inclusion.membershipId !== null
      ))
      ? "Free service"
      : promoFromCartDetails?.title ?? promoFromAvailablePromos?.title;

  const isMembershipDiscountApplied = discounts?.some(
    (d) => d.membershipId != null
  );

  const promoDiscount =
    cartDetails.orderSummaryItems?.find(
      (item) => item.name === "PromotionCodeDiscount"
    )?.value || 0;

  // Buy X Get Y Modal Logic
  const maxDiscountPromo = availablePromos?.length
    ? availablePromos.reduce(
        (max, current) =>
          current.totalDiscount > max.totalDiscount ? current : max,
        availablePromos[0]
      )
    : undefined;
  const isMaxDiscountBuyXGetY =
    buyXGetYFlag && isBuyXGetYDiscount(maxDiscountPromo?.discount);

  const getBXGYDiscountTextForModal =
    isMaxDiscountBuyXGetY &&
    maxDiscountPromo?.discount &&
    getBXGYDiscountText(maxDiscountPromo.discount, listings);

  const checkoutButtonClick = async () => {
    setIsCheckingOut(true); // let's disable the button to prevent double clicks

    const numberOfItems = cartDetails.shopDisplayOrderLineItems?.length;
    const archivedRemoved = await removeArchiveItems(cartDetails);
    const discountApplied =
      promoDiscount > 0 ? promoDiscount : membershipDiscount?.totalDiscount;
    const safeDiscountApplied = discountApplied ?? 0;
    if (numberOfItems !== archivedRemoved) {
      const hasValidPromo =
        availablePromos?.find(
          (promo) => promo.totalDiscount > safeDiscountApplied
        ) || (membershipDiscount?.totalDiscount ?? 0) > safeDiscountApplied;

      const originalCartValue =
        cartDetails.estimatedTotal + safeDiscountApplied;

      // Check if the current discount covers the entire original order value
      const currentDiscountCoversEntireOrder =
        safeDiscountApplied >= originalCartValue;

      // Check if the current BXGY discount is already applied
      const isBXGYAlreadyApplied =
        isMaxDiscountBuyXGetY &&
        maxDiscountPromo &&
        discounts?.some((d) => d.discountId === maxDiscountPromo.discountId);

      if (
        ((!isMembershipDiscountApplied &&
          !coupon &&
          availablePromos?.length &&
          preventCheckout) ||
          (hasValidPromo && preventCheckout)) &&
        !isBXGYAlreadyApplied &&
        !currentDiscountCoversEntireOrder
      ) {
        setModal({
          type: isMaxDiscountBuyXGetY
            ? "buy-x-get-y-offer"
            : "select-promo-code",
        });
        setPreventCheckout(false);
        if (hasValidPromo && coupon) {
          setHasMoreDiscounts(true);
        }

        setIsCheckingOut(false);
      } else {
        navigate(checkoutActionPath);
      }
    }
  };

  const getUnavailableItemsMessage = (
    locationId: string
  ): string | string[] => {
    if (!lineItems || !locationId) return "";

    const unavailableItems = lineItems.filter(
      (item) => !item.availableLocations?.includes(locationId)
    );

    if (unavailableItems.length === 0) return "";
    if (unavailableItems.length === 1) return unavailableItems[0].productName;

    return unavailableItems.map((item) => item.productName);
  };

  const isLocationAvailable = (locationId: string) => {
    if (!lineItems.length) {
      return true;
    }
    return lineItems.every(({ availableLocations }) =>
      availableLocations?.includes(locationId)
    );
  };

  const isMultiLocationPlatform = locations?.length && locations?.length >= 2;

  const checkItemAndPromoAtLocation =
    isSelectPatientLocationEnabled &&
    isMultiLocationPlatform &&
    (!allItemsAvailableAtSelectedLocation ||
      !hasValidPromoRewardOrMembershipBenefit);

  const getPromoRewardOrMembershipBenefitTitle = () => {
    if (hasInvalidMembershipApplied && !promoCode) {
      return unavailableMembership?.membershipName + " membership pricing";
    }
    return rewardId ? "reward" : "promotion";
  };

  const removeUnavailableMembership = () => {
    if (unavailableMembership) {
      setStoredMembership(null);
    }
  };

  const shouldShowLocationNameForInvalidPromo =
    displayedLocation?.name && // This ensures name is truthy (not null, undefined, or empty)
    promoCodeError;

  const cartHasRetailProducts = cartDetails.shopDisplayOrderLineItems?.every(
    (item) =>
      item.displayType == ShopDisplayType.Product ||
      item.displayType == ShopDisplayType.ProductFromExternalSource
  );

  const cartHasDeliveryProductsOnly =
    cartDetails.shopDisplayOrderLineItems?.every(
      (item) => item.displayType == ShopDisplayType.ProductFromExternalSource
    );

  const giftCardOnlyCart =
    cartDetails.shopDisplayOrderLineItems?.every(
      (item) => item.displayType === ShopDisplayType.GiftCard
    ) ?? false;

  return (
    <>
      <GiftCardRedemptionModal
        open={showGiftCardRecipientMissingModal}
        onClose={() => setSearchParams({}, { replace: true })}
        giftCards={giftCardListing}
        giftCardPrice={giftCardPrice}
        onSubmit={async (data) => {
          const isMySelf = data.recipientType.value === "myself";
          await mutateCreateAndLinkGiftCardRecipient.mutateAsync({
            shopDisplayOrderLineItemId: shopDisplayOrderLineItemId ?? "",
            phoneNumber: isMySelf
              ? platformUser?.phoneNumber ?? ""
              : data.phoneNumber
              ? `+1${removeNonDigits(data.phoneNumber)}`
              : "",
            email: isMySelf ? platformUser?.email ?? "" : data.email ?? "",
            firstName: isMySelf
              ? platformUser?.firstName ?? ""
              : data.firstName ?? "",
            lastName: isMySelf
              ? platformUser?.lastName ?? ""
              : data.lastName ?? "",
            giftCardItemId: data.giftCardItemId ?? "",
            mySelf: isMySelf,
          });
        }}
      />
      {isFetching && state === "idle" && (
        <div className="absolute inset-0 flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      )}
      <CartTopNav showNavbar={showNavbar} />
      <ShippingNotification
        current={cartDetails.freeShippingProgress}
        eligible={cartDetails.shippingFee == 0}
        threshold={cartDetails.freeShippingThreshold}
      />
      {!!cartItems?.length && (
        <CartItemList cartItems={cartItems}></CartItemList>
      )}
      {reservationTimeRemaining && (
        <div className="mt-4 bg-pink-100 px-4 py-3">
          <div className="text-center text-sm font-bold text-gray-800">
            Your reservation will expire in {reservationTimeRemaining.minutes}:
            {reservationTimeRemaining.seconds.toString().padStart(2, "0")}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3 p-4">
        <AvailablePromotions
          checkoutPromoModalOpen={modal?.type === "select-promo-code"}
          buyXgetYPromotionTitle={
            (modal?.type === "buy-x-get-y-offer" &&
              getBXGYDiscountTextForModal) ||
            ""
          }
          buyXgetYPromoCode={
            isMaxDiscountBuyXGetY ? maxDiscountPromo?.promoCode : ""
          }
          setCheckoutPromoModalOpen={() => setModal(null)}
          checkoutButtonLabel="Continue to checkout"
          checkoutButtonClick={checkoutButtonClick}
          hasMoreDiscounts={hasMoreDiscounts}
          coupon={coupon}
          rewardId={rewardId}
          discountId={discountId}
          rewardPromos={availablePromos?.filter((p) => p.totalDiscount > 0)}
          setCoupon={function noop() {
            /* No-op since coupon is derived */
          }}
          setRewardId={function noop() {
            /* No-op since rewardId is derived */
          }}
          setDiscountId={function noop() {
            /* No-op since discountId is derived */
          }}
          onPromoSubmit={(promoCode, shouldLoad?: boolean) => {
            setActionSuccessMessage("");
            if (shouldLoad) setLoading(true);
            submit(
              {
                action: "promoCodeAction",
                promoCode: promoCode,
              },
              {
                method: "post",
              }
            );
            removeUnavailableMembership();
          }}
          onDelete={() => {
            if (promoCode?.promoCodeId) {
              submit(
                {
                  action: "removePromoCodeAction",
                  promoCodeId: promoCode?.promoCodeId ?? "",
                },
                {
                  method: "delete",
                }
              );
            }
            removeUnavailableMembership();
          }}
          onRewardSubmit={(rewardId) => {
            setActionSuccessMessage("");
            if (state == "idle") {
              submit(
                {
                  action: "rewardAction",
                  rewardId: rewardId,
                },
                {
                  method: "post",
                }
              );
            }
            removeUnavailableMembership();
          }}
          successMsg={successMessage}
          externalPromoAmountOff={couponAmountOff}
          errorMsg={
            actionData?.action === "promoCodeAction"
              ? actionData.error
              : undefined
          }
          successMsgOnDelete={
            actionData?.action === "deletePromoCode"
              ? actionData.deleted
              : false
          }
          appliedPromoTitle={getPromoCodeTitle}
          membershipDiscount={
            membershipDiscount as Models<"GetMembershipDetailsResult">
          }
          isMembershipDiscountApplied={!!isMembershipDiscountApplied}
          loading={loading}
          locationName={
            (shouldShowLocationNameForInvalidPromo ||
              (hasInvalidMembershipApplied && displayedLocation?.name)) &&
            displayedLocation?.name != null
              ? displayedLocation.name
              : undefined
          }
          openPromotionsModal={shouldDisplayPromotionModal}
          togglePromotionModal={setShouldDisplayPromotionModal}
          unavailableMembership={unavailableMembership}
          hasInvalidMembershipApplied={hasInvalidMembershipApplied}
        />
        {platform.platformSettings?.repeatCashEnabled &&
          !!cartDetails.platformUser?.repeatCashBalance &&
          !cartDetails.shopDisplayOrderLineItems?.every(
            (item) => item.displayType === ShopDisplayType.GiftCard
          ) && (
            <div className="flex flex-col gap-6">
              <ApplyRepeatMDCash
                repeatCashBalance={cartDetails.platformUser?.repeatCashBalance}
                repeatCashOnCart={cartDetails.repeatCashAppliedToCart}
                isApplied={cartDetails.repeatCashAppliedToCart > 0}
                cartDetails={cartDetails}
                onApply={(amount) => {
                  submit(
                    {
                      action: "applyCash",
                      amount: parsePriceString(amount),
                    },
                    { method: "put" }
                  );
                }}
              />
            </div>
          )}
      </div>
      {isSelectPatientLocationEnabled &&
        isMultiLocationPlatform &&
        cartDetails &&
        !cartHasDeliveryProductsOnly && (
          <LocationDetailsCard
            cartHasRetailProducts={cartHasRetailProducts}
            onLocationChangeRequest={() => {
              setIsCheckingOut(true);
              setIsLocationChangeModalOpen(true);
            }}
            displayedLocationName={
              displayedLocation?.name ?? "Unknown Location"
            }
          />
        )}
      {salesAssociates.length > 0 && (
        <div className="flex flex-col">
          <div className="bg-two pt-2" />
          <div className="p-4">
            <Dropdown
              label="Who helped you with this purchase?"
              options={[
                { id: "", name: "Who helped you with this purchase?" },
                ...salesAssociates.map((salesAssociate) => ({
                  id: salesAssociate.id,
                  name: `${salesAssociate.firstName} ${salesAssociate.lastName}`,
                })),
              ]}
              value={salesAssociateValue}
              setValue={(salesAssociateId) => {
                if (salesAssociateId == "") {
                  submit(
                    {
                      action: "deleteSalesAssociate",
                    },
                    { method: "delete" }
                  );
                } else {
                  submit(
                    {
                      action: "setSalesAssociate",
                      salesAssociateId: salesAssociateId,
                    },
                    { method: "post" }
                  );
                }
              }}
              textValue={salesAssociateValueText}
            />
          </div>
        </div>
      )}
      <div className="bg-two pt-2" />
      {!!cartDetails.rewardSummary?.redeemableRewardCount && (
        <div className="px-4 pt-4">
          <RewardUnlockBanner
            rewardCount={cartDetails.rewardSummary.redeemableRewardCount}
            rewards={cartDetails.rewardSummary?.rewards}
          />
        </div>
      )}
      <div className="flex flex-col px-5 pt-5">
        <OrderSummary cart={cartDetails} displayProducts={false} />
        {platform.platformSettings?.affirmEnabled && (
          <>
            {(!isOrderTotalAboveMinimum ||
              (containsMembership && containsPackageOrTreatment) ||
              containsGiftCard) &&
              !(
                platform.platformSettings?.repeatCashEnabled &&
                !!cartDetails.platformUser?.repeatCashBalance &&
                containsGiftCard &&
                !giftCardOnlyCart
              ) &&
              !(
                platform.platformSettings?.repeatCashEnabled &&
                !!cartDetails.platformUser?.repeatCashBalance &&
                giftCardOnlyCart
              ) && (
                <div className="mt-5">
                  <NotAvailableAffirm
                    isLargeBanner={
                      containsMembership && containsPackageOrTreatment
                    }
                    reason={
                      containsGiftCard
                        ? AffirmUnavailableReason.ContainsGiftCard
                        : containsMembership && containsPackageOrTreatment
                        ? AffirmUnavailableReason.ContainsMembership
                        : AffirmUnavailableReason.BelowMinimum
                    }
                  />
                </div>
              )}
            {!containsGiftCard &&
              !containsMembership &&
              isOrderTotalAboveMinimum && (
                <div className="pt-5">
                  <ShopNowAffirm />
                </div>
              )}
          </>
        )}
        {platform.platformSettings?.repeatCashEnabled &&
          !!cartDetails.platformUser?.repeatCashBalance &&
          containsGiftCard &&
          !(
            platform.platformSettings?.affirmEnabled &&
            containsGiftCard &&
            !giftCardOnlyCart
          ) &&
          !(platform.platformSettings?.affirmEnabled && giftCardOnlyCart) && (
            <RepeatCashAvailabilityBanner
              message={
                giftCardOnlyCart
                  ? "is not available for this purchase."
                  : "will apply only to eligible items."
              }
            />
          )}
        {platform.platformSettings?.affirmEnabled &&
          platform.platformSettings?.repeatCashEnabled &&
          !!cartDetails.platformUser?.repeatCashBalance &&
          containsGiftCard &&
          !giftCardOnlyCart && (
            <div className="mt-5">
              <div className="items-center rounded bg-background-two py-2.5">
                <div className="flex items-center">
                  <div className="pl-3">
                    <Icon svg={InfoIcon} size="xsmall" color="secondaryText" />
                  </div>
                  <div className="flex flex-col">
                    <div className="pl-3 pr-4 text-body2">
                      <span className="text-bold2">Affirm</span> financing is
                      not available for this purchase.{" "}
                      <span className="text-bold2">RepeatCash</span> will apply
                      only to eligible items.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        {platform.platformSettings?.affirmEnabled &&
          platform.platformSettings?.repeatCashEnabled &&
          !!cartDetails.platformUser?.repeatCashBalance &&
          giftCardOnlyCart && (
            <div className="mt-5">
              <div className="items-center rounded bg-background-two py-2.5">
                <div className="flex items-center">
                  <div className="pl-3">
                    <Icon svg={InfoIcon} size="xsmall" color="secondaryText" />
                  </div>
                  <div className="flex flex-col">
                    <div className="pl-3 pr-4 text-body2">
                      <span className="text-bold2">Affirm</span> financing and{" "}
                      <span className="text-bold2">RepeatCash</span> are not
                      available for this purchase.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        <TermsAndCondition
          hideText={
            !platform.platformSettings?.affirmEnabled ||
            containsGiftCard ||
            containsMembership
          }
        />
        <RepeatMDFooter />
      </div>
      <div className="sticky bottom-0 w-full bg-white p-4 pb-[calc(16px_+_env(safe-area-inset-bottom))]">
        <Button
          disabled={
            hasExpiredItem ||
            isCheckingOut ||
            !!checkItemAndPromoAtLocation ||
            hasGiftCardMissingRecipient
          }
          onClick={checkoutButtonClick}
          fullWidth
        >
          Checkout now
        </Button>
      </div>

      {packagesPrices && (
        <ModalSelectorList
          size="medium"
          open={modal?.type === "select-units"}
          title={"SELECT YOUR QUANTITY"}
          type="products"
          items={packagePriceChoices}
          checkedItems={[newPackagePriceId]}
          onClose={() => {
            setModal(null);
            setNewPackagePriceId("");
          }}
          firstButtonLabel="Apply"
          onFirstButtonClick={async () => {
            if (newPackagePriceId != originalPackagePriceId && shopDisplay) {
              setModal(null);
              submit(
                {
                  action: "editItem",
                  shopDisplayOrderLineItemId: editShopDisplayOrderLineItemId,
                  packageId: newShopDisplayPackageId,
                  packagePriceId: newPackagePriceId,
                  packageListingId: shopDisplay.id,
                },
                { method: "put" }
              );
            }
          }}
          firstButtonDisabled={
            modal?.type === "select-units" ? disableApplyButton : false
          }
          onSelect={setNewPackagePriceId}
        />
      )}

      {packages && (
        <ModalSelectorList
          size="fullscreen"
          open={modal?.type === "select-treatment"}
          title={"SELECT YOUR SERVICE"}
          type="products"
          items={packageChoices}
          checkedItems={[newShopDisplayPackageId]}
          onClose={() => {
            setModal(null);
            setNewShopDisplayPackageId("");
          }}
          firstButtonLabel={packagesPrices.length > 1 ? "Next" : "Apply"}
          onFirstButtonClick={async () => {
            const packagePriceId =
              newPackagePriceId == ""
                ? packagesPrices[0].id
                : newPackagePriceId;

            if (packagesPrices.length > 1) {
              setModal({ type: "select-units" });
            } else if (shopDisplay) {
              setModal(null);
              submit(
                {
                  action: "editItem",
                  shopDisplayOrderLineItemId: editShopDisplayOrderLineItemId,
                  packageId: newShopDisplayPackageId,
                  packagePriceId: packagePriceId,
                  packageListingId: shopDisplay.id,
                },
                { method: "put" }
              );
            }
          }}
          onSelect={(shopDisplayPackageId) => {
            setNewShopDisplayPackageId(shopDisplayPackageId);

            if (originalShopDisplayPackageId !== shopDisplayPackageId) {
              setNewPackagePriceId("");
            } else {
              setNewPackagePriceId(originalPackagePriceId);
            }
          }}
        />
      )}

      {modal?.type === "repeatcash-invalid" && (
        <UnableToCheckoutModal onClose={() => setModal(null)} />
      )}

      <ActionModal
        size="small"
        open={modal?.type === "expired-listing"}
        title="You have an expired plan in your cart!"
        description="Please remove the expired plan from your cart in order to proceed to checkout."
        firstButtonLabel="Continue"
        onFirstButtonClick={() => setModal(null)}
        onClose={() => setModal(null)}
      />

      <IfFlag flag="selectPatientLocation">
        <ActionModal
          size="medium"
          open={shouldShowUnavailableLocationModal}
          headerIconSvg={(props) => (
            <div className="-mt-8">
              <WarningIcon {...props} width="50" height="50" />
            </div>
          )}
          title={
            lineItems.filter(
              (item) =>
                !item.availableLocations?.includes(displayedLocation?.id ?? "")
            ).length > 1
              ? "Items in your cart are not available at this location"
              : "An item in your cart is not available at this location"
          }
          description={
            displayedLocation ? (
              <div className="text-center">
                {lineItems.filter(
                  (item) =>
                    !item.availableLocations?.includes(displayedLocation.id)
                ).length === 1 ? (
                  <div>
                    <span>The </span>
                    <span className="font-medium text-nearly-black">
                      {getUnavailableItemsMessage(displayedLocation.id)}
                    </span>
                    <span> is not offered</span>
                    <br />
                    at the
                    <span className="font-medium text-nearly-black">
                      {" "}
                      {displayedLocation.name}{" "}
                    </span>
                    location.
                    <div className="mx-auto mt-6 max-w-[260px]">
                      Please remove this item from your cart to checkout with
                      this order, or place this order with another location.
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto max-w-[260px]">
                    Please remove these items from your cart to checkout with
                    this order, or place this order with another location.
                  </div>
                )}
              </div>
            ) : (
              ""
            )
          }
          firstButtonLabel="Update location"
          onFirstButtonClick={() => {
            // First hide the warning modal, then show the location selection modal
            // setShowLocationUnavailableModal(false);
            // Short delay to ensure smooth transition between modals
            setTimeout(() => {
              setIsLocationChangeModalOpen(true);
              setHideUnavailableLocationModal(true);
            }, 100);
          }}
          onClose={() => {
            setHideUnavailableLocationModal(true);
          }}
          isCartPage={true}
        />

        <ActionModal
          size="medium"
          // we don't show the invalid promo warning modal unless we have all treatments available
          // at the selected location. If we don't have it available, there's a high chance that changing
          // the location will change the validty of the promo code as well.
          open={isInvalidPromotionAppliedModal}
          headerIconSvg={(props) => (
            <div className="-mt-8">
              <WarningIcon {...props} width="50" height="50" />
            </div>
          )}
          title={`Your current ${getPromoRewardOrMembershipBenefitTitle()} is not valid at this location`}
          description={
            <div className="text-center">
              <div>
                <span>The </span>
                <span className="font-medium text-nearly-black">
                  {getPromoCodeTitle}
                </span>
                <span>
                  {" "}
                  {getPromoRewardOrMembershipBenefitTitle()} is not valid
                </span>
                <br />
                at the
                <span className="font-medium text-nearly-black">
                  {" "}
                  {locationName}{" "}
                </span>
                location.
                <div className="mx-auto mt-6 max-w-[260px]">
                  Please select a different{" "}
                  {getPromoRewardOrMembershipBenefitTitle()} or remove the{" "}
                  <span className="font-medium text-nearly-black">
                    {getPromoCodeTitle}
                  </span>{" "}
                  to check out with this order.
                </div>
              </div>
            </div>
          }
          firstButtonLabel={`Remove ${getPromoRewardOrMembershipBenefitTitle()}`}
          onFirstButtonClick={() => {
            if (!unavailableMembership) {
              submit(
                {
                  action: "removePromoCodeAction",
                  promoCodeId: promoCode?.promoCodeId ?? "",
                },
                {
                  method: "delete",
                }
              );
            }
            removeUnavailableMembership();
            setHideInvalidPromotionModal(true);
          }}
          secondButtonLabel="View promotions"
          onSecondButtonClick={() => {
            setHideInvalidPromotionModal(true);

            // Short delay to ensure a smooth transition between modals
            setTimeout(() => {
              setShouldDisplayPromotionModal(true);
            }, 100);
          }}
          onClose={() => {
            setHideInvalidPromotionModal(true);
          }}
          isCartPage={true}
        />

        <ModalSelectorList
          size="medium"
          open={isLocationChangeModalOpen}
          title="Where will you book these services?"
          type="location"
          items={
            locations?.map((location) => {
              const isAvailable = isLocationAvailable(location.id);
              return {
                id: location.id,
                title: location.name ?? "",
                description: `${location.city}, ${location.state}, ${location.postalCode}`,
                showWarning: !isAvailable,
                subtitle: location.address1 ?? undefined,
              };
            }) ?? []
          }
          checkedItems={[selectedLocationId]}
          onClose={() => {
            setIsLocationChangeModalOpen(false);
            if (displayedLocation) {
              setSelectedLocationId(displayedLocation.id);
            }
            setIsCheckingOut(false);
          }}
          firstButtonLabel="Update location"
          onFirstButtonClick={async () => {
            submit(
              {
                action: "updateLocation",
                locationId: selectedLocationId,
                shopDisplayOrderId: cartDetails?.shopDisplayOrderId,
                rewardId: rewardId,
              },
              { method: "post" }
            );
            setIsLocationChangeModalOpen(false);
            setTimeout(() => {
              setIsCheckingOut(false);
            }, 1000);
          }}
          firstButtonDisabled={
            !selectedLocationId ||
            (displayedLocation
              ? selectedLocationId === displayedLocation.id
              : false)
          }
          onSelect={(locationId) => {
            setSelectedLocationId(locationId);
          }}
          errorMessage="An item in your cart is not offered at this location"
          displayError={!isLocationAvailable(selectedLocationId)}
        />
      </IfFlag>
    </>
  );
};
